import React, { useEffect, useRef, useState } from 'react';
import { community as commApi, communityApi, files as filesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { MessageCircle, Send, Image as ImageIcon, Loader2, X, Users, MessageSquare, MapPin } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import ReviewedLeaderboard from '../components/ReviewedLeaderboard';
import SiteSections from '../components/SiteSections';
import { MemberAvatarWithBadge, MemberTypeBadge } from '../components/MemberBadge';
import { Link } from 'react-router-dom';
import CommunityPostCard from '../components/CommunityPostCard';
import { JoinPromptDialog, MessageDialog, ReportDialog, BlockConfirmDialog } from '../components/CommunityDialogs';
import { Switch } from '../components/ui/switch';

const TABS = [
  { key: 'feed', label: 'Feed', icon: MessageSquare },
  { key: 'members', label: 'Members', icon: Users },
];

export default function Community() {
  const { user } = useAuth();
  const [tab, setTab] = useState('feed');

  // Feed state
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const inputRef = useRef(null);
  const [joinOpen, setJoinOpen] = useState(false);
  const [msgPost, setMsgPost] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);
  const [blockTarget, setBlockTarget] = useState(null);
  const [blockVersion, setBlockVersion] = useState(0);
  const [alertsOn, setAlertsOn] = useState(null);
  const safety = { report: setReportTarget, block: setBlockTarget };
  const reloadAfterBlock = () => { commApi.posts().then(setPosts); setBlockVersion(v => v + 1); };

  useEffect(() => { if (user) commApi.alertPrefs().then(d => setAlertsOn(d.commentAlerts)).catch(() => {}); }, [user]);
  const toggleAlerts = async (v) => {
    setAlertsOn(v);
    try { await commApi.setAlertPrefs(v); toast({ title: v ? 'Comment alerts on' : 'Comment alerts off' }); }
    catch { setAlertsOn(!v); toast({ title: 'Could not save that setting' }); }
  };

  // Members state — lazy-loaded the first time the Members tab opens.
  const [members, setMembers] = useState(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberFilter, setMemberFilter] = useState('All');

  useEffect(() => {
    commApi.posts().then(setPosts).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab !== 'members' || members !== null || membersLoading) return;
    setMembersLoading(true);
    communityApi.members(100)
      .then(setMembers)
      .catch(() => setMembers([]))
      .finally(() => setMembersLoading(false));
  }, [tab, members, membersLoading]);

  const pickImage = () => inputRef.current?.click();

  const onImageChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast({ title: 'Please choose an image file' }); return; }
    const previewUrl = URL.createObjectURL(f);
    setPendingImage({ file: f, previewUrl });
    setUploadingImg(true);
    try {
      const res = await filesApi.upload(f, 'post');
      setPendingImage((p) => ({ ...(p || {}), uploadedUrl: res.url }));
    } catch (err) {
      setPendingImage(null);
      toast({ title: 'Image upload failed', description: err?.response?.data?.detail || 'Try again' });
    } finally {
      setUploadingImg(false);
      e.target.value = '';
    }
  };

  const clearImage = () => {
    if (pendingImage?.previewUrl) URL.revokeObjectURL(pendingImage.previewUrl);
    setPendingImage(null);
  };

  const post = async () => {
    if (!text.trim() && !pendingImage?.uploadedUrl) {
      toast({ title: 'Add some text or a photo first' });
      return;
    }
    setPosting(true);
    try {
      const p = await commApi.create(text.trim(), pendingImage?.uploadedUrl || null);
      setPosts(prev => [p, ...prev]);
      setText('');
      clearImage();
    } catch (err) {
      // Surface the failure instead of letting the button go quiet — the
      // #1 cause of the "Post button isn't working" report is a swallowed error.
      const detail = err?.response?.data?.detail || err?.message || 'Please try again.';
      toast({ title: 'Post failed', description: detail });
    } finally {
      setPosting(false);
    }
  };

  const like = async (id) => {
    try {
      const updated = await commApi.like(id);
      setPosts(prev => prev.map(p => p.id === id ? { ...updated, comments: p.comments } : p));
    } catch (err) {
      toast({ title: 'Like failed', description: err?.response?.data?.detail || 'Please try again.' });
    }
  };

  const bumpComments = (id) => setPosts(prev => prev.map(p => p.id === id ? { ...p, comments: (p.comments || 0) + 1 } : p));

  return (
    <div className={user ? '' : 'bg-black text-white min-h-[70vh] px-4 py-8'} data-testid={user ? 'community-member-view' : 'community-visitor-view'}>
    <div className="max-w-3xl mx-auto space-y-5">
      <div>
        <h1 className="text-2xl font-bold">Community</h1>
        <p className="text-gray-400 text-sm">Connect, share, and celebrate wins with the PBB family.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#1f1f1f]" data-testid="community-tabs">
        {TABS.filter(t => user || t.key === 'feed').map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`community-tab-${t.key}`}
              className={`relative inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold transition-colors ${active ? 'text-green-400' : 'text-gray-400 hover:text-gray-200'}`}
            >
              <Icon size={14}/> {t.label}
              {active && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-green-400"/>}
            </button>
          );
        })}
      </div>

      {tab === 'feed' && (
        <div className="space-y-5" data-testid="community-feed-tab">
          {!user && (
            <div className="pbb-card p-5 border border-green-500/30 bg-green-500/5" data-testid="community-visitor-banner">
              <div className="font-semibold text-white">You're viewing the community as a guest</div>
              <p className="mt-1 text-sm text-gray-300">Join free to post, comment, like, and message members.</p>
              <div className="mt-3 flex gap-2">
                <Link to="/signup" data-testid="community-visitor-join" className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-sm font-semibold text-white">Join free</Link>
                <Link to="/login?redirect=/community" data-testid="community-visitor-login" className="px-4 py-2 rounded-lg border border-[#2a2a2a] hover:border-green-500 text-sm text-white">Sign in</Link>
              </div>
            </div>
          )}
          {user && alertsOn !== null && (
            <label className="flex items-center justify-between gap-3 pbb-card px-4 py-3 text-sm" data-testid="comment-alerts-row">
              <span><span className="font-semibold text-white">Comment alerts</span><span className="block text-xs text-gray-300">Get a bell alert when someone comments on your post or a post you commented on.</span></span>
              <Switch checked={alertsOn} onCheckedChange={toggleAlerts} data-testid="comment-alerts-toggle" />
            </label>
          )}
          {user && <ReviewedLeaderboard window="week" limit={5} />}
          {user && <div className="pbb-card p-4">
            <div className="flex items-start gap-3">
              <img src={filesApi.urlFor(user?.avatar)} alt="me" className="w-10 h-10 rounded-full object-cover" />
              <div className="flex-1">
                <textarea rows={2} value={text} onChange={e => setText(e.target.value)}
                  className="pbb-input" placeholder="Share something with the community..." />
                {pendingImage && (
                  <div className="relative mt-3 inline-block">
                    <img src={pendingImage.previewUrl} alt="preview" className="max-h-40 rounded-lg" />
                    {uploadingImg && (
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                        <Loader2 className="text-white animate-spin" size={22}/>
                      </div>
                    )}
                    <button onClick={clearImage} className="absolute -top-2 -right-2 w-6 h-6 bg-black border border-[#2a2a2a] rounded-full flex items-center justify-center text-gray-300 hover:text-white">
                      <X size={14}/>
                    </button>
                  </div>
                )}
                <div className="flex items-center justify-between mt-3">
                  <button onClick={pickImage} disabled={uploadingImg} className="text-gray-400 hover:text-green-400 text-sm inline-flex items-center gap-1 disabled:opacity-60">
                    <ImageIcon size={16}/> Photo
                  </button>
                  <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onImageChange}/>
                  <button onClick={post} disabled={posting || uploadingImg} data-testid="community-post-submit" className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-sm text-white inline-flex items-center gap-2 disabled:opacity-70">
                    {posting ? <Loader2 className="animate-spin" size={14}/> : <Send size={14}/>} Post
                  </button>
                </div>
              </div>
            </div>
          </div>}
          {loading ? (
            <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading...</div>
          ) : posts.length === 0 ? (
            <div className="pbb-card border-2 border-dashed border-green-500/30 bg-green-500/5 p-10 text-center" data-testid="community-empty-state">
              <MessageCircle className="mx-auto text-green-400" size={30} />
              <h3 className="mt-4 text-xl font-bold">Be the first voice in the community</h3>
              <p className="mt-2 text-sm text-gray-300 max-w-md mx-auto">Share your win, welcome new members, or drop a business shout-out. Founding members shape the culture from day one.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map(p => (
                <CommunityPostCard key={`${p.id}-${blockVersion}`} post={p} viewer={user} onLike={like} safety={safety}
                  onRequireJoin={() => setJoinOpen(true)} onMessage={setMsgPost} onCommentAdded={bumpComments} />
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'members' && (
        <MembersDirectory
          members={members}
          loading={membersLoading}
          filter={memberFilter}
          onFilterChange={setMemberFilter}
        />
      )}

      <SiteSections pageKey="community" />
      <JoinPromptDialog open={joinOpen} onClose={() => setJoinOpen(false)} />
      <MessageDialog post={msgPost} onClose={() => setMsgPost(null)} />
      <ReportDialog target={reportTarget} onClose={() => setReportTarget(null)} onDone={(blocked) => blocked && reloadAfterBlock()} />
      <BlockConfirmDialog target={blockTarget} onClose={() => setBlockTarget(null)} onBlocked={reloadAfterBlock} />
    </div>
    </div>
  );
}

const MEMBER_FILTERS = ['All', 'Business Owner', 'Consumer', 'Nonprofit', 'Youth Leader', 'Author', 'Group', 'Artist'];

function MembersDirectory({ members, loading, filter, onFilterChange }) {
  if (loading || members === null) {
    return (
      <div className="pbb-card p-10 text-center" data-testid="community-members-loading">
        <Loader2 className="mx-auto animate-spin text-green-400" size={22}/>
      </div>
    );
  }

  const filtered = filter === 'All'
    ? members
    : members.filter(m => (m.memberType === filter) || (filter === 'Youth Leader' && m.memberType === 'Young Leader'));

  return (
    <div className="space-y-4" data-testid="community-members-tab">
      {/* Filter pills */}
      <div className="flex flex-wrap gap-2" data-testid="community-members-filters">
        {MEMBER_FILTERS.map(f => (
          <button
            key={f}
            onClick={() => onFilterChange(f)}
            data-testid={`community-members-filter-${f.replace(/\s/g, '-').toLowerCase()}`}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${filter === f ? 'bg-green-500 text-black border-green-500' : 'border-[#2a2a2a] text-gray-300 hover:border-green-500/50'}`}
          >
            {f}
          </button>
        ))}
        <div className="ml-auto text-xs text-gray-500 self-center">{filtered.length} member{filtered.length === 1 ? '' : 's'}</div>
      </div>

      {filtered.length === 0 ? (
        <div className="pbb-card p-10 text-center">
          <Users className="mx-auto text-green-400" size={28}/>
          <div className="mt-3 font-semibold">No members match this filter</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="community-members-grid">
          {filtered.map(m => (
            <div key={m.id} className="pbb-card p-4 flex flex-col items-center text-center" data-testid={`community-member-card-${m.id}`}>
              <MemberAvatarWithBadge
                initial={m.initial}
                memberType={m.memberType}
                avatarUrl={m.avatarUrl}
                size={72}
              />
              <div className="mt-3 font-semibold text-sm text-white truncate w-full">{m.name}</div>
              <MemberTypeBadge type={m.memberType} className="mt-1.5" size={22} />
              <div className="mt-1 text-[11px] text-gray-500 uppercase tracking-wider">{m.memberType}</div>
              {m.city && (
                <div className="mt-1 text-[11px] text-gray-500 inline-flex items-center gap-1">
                  <MapPin size={10}/> {m.city}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
